import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { QueryBuilderConfig, QueryBuilderClassNames } from 'src/app/shared/query-builder';
import { QueryBuilderService } from 'src/app/shared/query-builder/query-builder.service';
import { sortBy } from 'lodash';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { Dictionary } from 'src/app/core/model/dictionary';
import { forkJoin } from 'rxjs';
import { RuleService } from 'src/app/core/service/rule.service';

@Component({
  standalone: false,
  selector: 'app-formly-designer-rule-edit',
  templateUrl: './formly-designer-rule-edit.component.html',
  styleUrls: ['./formly-designer-rule-edit.component.scss']
})
export class FormlyDesignerRuleEditComponent implements OnInit
{
  @Input() public query: any;
  @Input() public fields: FormlyFieldConfig[];
  @Input() public ruleIndex: number;
  @Input() public showRules = false;
  @Input() public isDeleted = false;
  @Input() public isLocked = false;
  @Input() public expandWidth = '190%';

  @Output() public showRulesChanged: EventEmitter<any> = new EventEmitter();

  public queryConfig: QueryBuilderConfig;
  public classNames: QueryBuilderClassNames;

  private loadingFields = [];
  private dictionaries = new Map();

  constructor(
    private queryBuilderService: QueryBuilderService,
    private ruleService: RuleService,
    private messageHandlerService: MessageHandlerService,
    private dictionaryService: DictionaryService)
  {
    this.classNames = this.queryBuilderService.classNames();
    this.classNames.fieldControl = 'form-control assessment-control-field';
    this.classNames.inputControl = 'form-control assessment-control-value';
  }

  public ngOnInit(): void
  {
    this.convertFormlyFields2QueryBuilder();
  }

  public setShowRules(showRules: boolean): void
  {
    this.showRules = showRules;
    this.showRulesChanged.emit({ show: showRules, index: this.ruleIndex });
  }

  /**
   * Converting formly-fields to query-builder configuration.
   */
  private convertFormlyFields2QueryBuilder(): void
  {
    // Getting any dictionaries first
    const dictionaryObservables: any = this.ruleService.getFormlyFieldDictionaries(this.fields);

    // Load dictionaries identified
    this.dictionaries = new Map();
    if (dictionaryObservables.length > 0)
    {
      forkJoin(dictionaryObservables).subscribe({
        next: (dictionaryData) =>
        {
          Object.keys(dictionaryData).forEach(e =>
          {
            // Check if we have dictionaries and not already loaded
            if (dictionaryData[e].length > 0 && !this.dictionaries.has(dictionaryData[e][0].codeSetId)) 
            {
              this.dictionaries.set(dictionaryData[e][0].codeSetId, sortBy(dictionaryData[e], ['displayName']));
            }
          });
          this.startLoadingFormlyFields();
        },
        error: (errorResponse: any) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
    else
    {
      this.startLoadingFormlyFields();
    }
  }

  private startLoadingFormlyFields(): void 
  {
    // Processing fields
    this.loadingFields = [];
    this.queryConfig = null;

    this.fields.forEach(d =>
    {
      if (d.type !== undefined)
      {
        if (d.type === 'tabs' && d.fieldGroup) 
        {
          d.fieldGroup.forEach((tabGroup) =>
          {
            if (tabGroup.fieldGroup) { tabGroup.fieldGroup.forEach((fieldGroup) => { this.processFormlyField(fieldGroup); }); }
          });
        }
        else 
        {
          this.processFormlyField(d);
        }
      }
    });
    if (this.loadingFields.length > 0)
    {
      this.queryConfig = this.queryBuilderService.transform(this.loadingFields);
    }
  }

  /**
   * Locate all dictionaries that are included in the Assessment fields
   * 
   * @param d Assessment data
   * @param dictionaries Dictionaries
   * @returns Array of dictionaries
   */
  private processFormlyFieldDictionaries(d: any, dictionaries: any[]): any[] 
  {
    if ((d.type === 'select' || d.type === 'multiselect') && d.templateOptions && d.templateOptions.codeSetId) 
    {
      dictionaries.push(this.dictionaryService.getCachedCodesByCodeSetId(d.templateOptions.codeSetId));
    }
    return dictionaries;
  }

  private processFormlyField(d: any): void
  {
    const field: any = {};
    switch (d.type)
    {
      // Supported formly-fields
      case 'input':
      case 'radio':
      case 'select':
      case 'multiselect':
      case 'date':
      case 'datetime':
      case 'checkbox':
      case 'multicheckbox':
        field.id = d.key;
        field.name = d.templateOptions.label;
        field.options = [];
        if (d.type === 'select' || d.type === 'radio' || d.type === 'multicheckbox' || d.type === 'multiselect')
        {
          field.type = 'Category';
          d.templateOptions.options.forEach((o: any) =>
          {
            field.options.push({ name: o.label, value: o.value });
          });
          field.options = sortBy(field.options, ['name']);

          // Adding dictionary entries if codeSetId is specified
          if ((d.type === 'select' || d.type === 'multiselect') && d.templateOptions.codeSetId && this.dictionaries.has(d.templateOptions.codeSetId))
          {
            this.dictionaries.get(d.templateOptions.codeSetId).forEach((dictionary: Dictionary) =>
            {
              field.options.push({ name: dictionary.displayName, value: dictionary.code });
            });
          }
        }
        else if (d.type === 'checkbox')
        {
          field.type = 'Boolean';
        }
        else if (d.type === 'date')
        {
          field.type = 'Date';
        }
        else if (d.type === 'datetime')
        {
          field.type = 'DateTime';
        }
        else
        {
          field.type = (d.templateOptions && d.templateOptions.type === 'number') ? 'Number' : 'String';
        }
        this.loadingFields.push(field);
        break;
    }
  }
}
